/**
 * `<policy-pattern-list>` — the one wildcard-pattern field, and the field that
 * refuses a dotted table name (`SC-F507`, `SC-S506`, `SC-S507`).
 *
 * Three things happen here that a plain text list cannot do:
 *
 *  1. **A dotted value is refused outright**, with the runtime reason: matching is
 *     against the bare relation name and the wildcard compiler escapes the dot to
 *     a literal, so the dotted form can never match. Beside the refusal sits the
 *     offer to split it into catalog / schema / table — the element does not own
 *     those three lists, so it asks its parent to apply the split.
 *  2. **A pattern preview**, backed by the schema cache, lists what the pattern
 *     currently resolves to with an explicit *matches nothing* state in warning
 *     treatment. Nothing is not an empty list: every soft reference in the DSL
 *     fails by never matching rather than by erroring, so "nothing" is the answer
 *     an author most needs to see. When the cache is empty or stale the preview
 *     says so and shows no result, rather than a possibly wrong one.
 *  3. **The preview never gates a save.** It is an advisory mirror of Sluice's
 *     matcher (`03` §3.14) and is labelled as one. A refusal here comes only from
 *     the two faults Sluice itself refuses at compile — an empty pattern and a
 *     trailing lone backslash — and from the dotted value the linter blocks.
 *
 * The grammar help is passed in from the form schema rather than written here, so
 * the sentence a reviewer approved in `form-schema.json` is the sentence the author
 * reads.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "../../components/index.js";
import type { PreviewTable } from "./schema-cache.js";
import type { PolicyFinding } from "../../api/policies.js";
import {
  compileWildcard,
  dottedFault,
  isFault,
  resolvePatterns,
  splitDotted,
  type DottedSplit,
  type PatternFault,
  type PatternScope,
} from "./patterns.js";
import { diagnosticSeverity, findingsAt, worst } from "./policy-findings.js";

/** Detail of `patterns-change`: the whole list, empty entries removed. */
export interface PatternListChangeDetail {
  values: string[];
}

/** Detail of the `pattern-split` event: the dotted value the author accepted a
 * split for, and the three parts to distribute. */
export interface PatternSplitDetail {
  value: string;
  split: DottedSplit;
}

@customElement("policy-pattern-list")
export class PolicyPatternList extends LitElement {
  /** The visible label — `Tables`, `Catalogs`, … */
  @property() label = "";
  /** Which axis of the schema cache the preview resolves against. */
  @property() scope: PatternScope = "tables";
  /** The patterns. Property-only; an empty array renders one blank row. */
  @property({ attribute: false }) values: string[] = [];
  /** The flattened schema cache. Empty plus `cacheEmpty` is the honest unknown. */
  @property({ attribute: false }) tables: PreviewTable[] = [];
  /** True when the control plane has no schema to answer from, so the preview
   * says so instead of reporting "matches nothing". */
  @property({ type: Boolean }) cacheEmpty = false;
  /** The cache's own sentence about its freshness, shown verbatim. */
  @property() cacheWarning = "";
  /** The field's help text, from the form schema. */
  @property() help = "";
  /** The wildcard grammar statement, from the form schema's group help. */
  @property() grammar = "";
  /** True for the two fields matched against a bare name, where a dot is a
   * refusal rather than a segment separator. */
  @property({ type: Boolean, attribute: "no-dots" }) noDots = false;
  /** Renders every row disabled — for a field the pinned build does not read. */
  @property({ type: Boolean }) disabled = false;
  /** The object path this list sits at, so linter findings anchored to
   * `…tables[1]` land on the right row. */
  @property() path = "";
  /** Findings for this list and its entries. */
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  /** Suppresses the resolution preview, for an axis the cache cannot answer —
   * `tags` resolve through another object's rules, not through the schema. */
  @property({ type: Boolean, attribute: "no-preview" }) noPreview = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .row {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-xs);
    }
    .row bn-input {
      flex: 1 1 auto;
      min-width: 0;
    }
    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: center;
      margin-top: var(--bino-space-xs);
    }
    .fix {
      border: 1px solid var(--bino-warning-border);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-sm);
      margin: var(--bino-space-xs) 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-base);
    }
    .fix p {
      margin: 0 0 var(--bino-space-sm);
      max-width: 80ch;
    }
    .preview {
      margin-top: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .preview.none {
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs) var(--bino-space-sm);
    }
    .advisory {
      display: block;
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
    }
    ul.names {
      list-style: none;
      margin: var(--bino-space-xs) 0 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs);
    }
    ul.names li {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius-sm);
      padding: 0 var(--bino-space-xs);
      overflow-wrap: anywhere;
    }
    details {
      margin-top: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    summary {
      cursor: pointer;
      width: fit-content;
      border-radius: var(--bino-radius-sm);
    }
    summary:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    details p {
      margin: var(--bino-space-xs) 0 0;
      max-width: 80ch;
    }
  `;

  /** The rows to render: the values, plus one blank row so the field is never a
   * dead end. A disabled list shows only what is actually there. */
  private get rows(): string[] {
    if (this.disabled) return this.values.length > 0 ? this.values : [""];
    return [...this.values, ""];
  }

  private emit(values: string[]) {
    this.dispatchEvent(
      new CustomEvent<PatternListChangeDetail>("patterns-change", {
        detail: { values: values.filter((v) => v !== "") },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onInput(index: number, value: string) {
    const next = [...this.values];
    if (index < next.length) next[index] = value;
    else next.push(value);
    this.emit(next);
  }

  private removeAt(index: number) {
    this.emit(this.values.filter((_, i) => i !== index));
  }

  private acceptSplit(value: string) {
    this.dispatchEvent(
      new CustomEvent<PatternSplitDetail>("pattern-split", {
        detail: { value, split: splitDotted(value) },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** The fault on one entry, in the order the author needs to hear them: the
   * dotted value first, because it is the one with a one-click fix. */
  private faultFor(value: string): PatternFault | null {
    if (value === "") return null;
    if (this.noDots) {
      const dotted = dottedFault(value, this.scope === "columns" ? "column" : "table");
      if (dotted) return dotted;
    }
    const compiled = compileWildcard(value);
    return isFault(compiled) ? compiled : null;
  }

  /** The linter finding anchored to one entry, e.g. `…tables[1]`. */
  private findingFor(index: number): PolicyFinding | null {
    if (!this.path) return null;
    return worst(findingsAt(this.findings, `${this.path}[${index}]`));
  }

  private renderRow(value: string, index: number) {
    const fault = this.faultFor(value);
    const finding = this.findingFor(index);
    // A client-side fault wins the message slot over a server finding, because it
    // is the same defect stated with the fix attached; the rule id is kept so the
    // two read as one rule and not two opinions.
    const message = fault?.message ?? finding?.message ?? "";
    const rule = fault?.rule || finding?.rule || "";
    const severity = fault ? "error" : finding ? diagnosticSeverity(finding.severity) : "error";
    const last = index === this.values.length;
    return html`
      <div class="row">
        <bn-input
          mono
          aria-label=${`${this.label} pattern ${index + 1}`}
          placeholder=${last ? "Add a pattern…" : ""}
          .value=${value}
          ?disabled=${this.disabled}
          diagnostic=${message}
          rule=${rule}
          severity=${severity}
          @input=${(e: CustomEvent<{ value: string }>) => {
            // bn-input re-dispatches a composed CustomEvent, which would escape
            // this shadow root and reach the editor as an unrelated `input`.
            e.stopPropagation();
            this.onInput(index, e.detail.value);
          }}
        ></bn-input>
        ${!this.disabled && !last
          ? html`<bn-button
              size="sm"
              variant="ghost"
              @click=${() => this.removeAt(index)}
              aria-label=${`Remove ${this.label} pattern ${value}`}
            >
              Remove
            </bn-button>`
          : nothing}
      </div>
      ${fault?.rule === "L01" ? this.renderSplitOffer(value) : nothing}
    `;
  }

  /** The offer `SC-S506` asks for: not just a refusal, the three fields the value
   * belongs in. */
  private renderSplitOffer(value: string) {
    const split = splitDotted(value);
    return html`<div class="fix" role="group" aria-label="Split this dotted value">
      <p>
        <code>${value}</code> can be expressed as
        ${split.catalog ? html`catalog <code>${split.catalog}</code>, ` : nothing}
        ${split.schema ? html`schema <code>${split.schema}</code>, ` : nothing} table
        <code>${split.table}</code>.
      </p>
      <bn-button size="sm" @click=${() => this.acceptSplit(value)}>
        Split into catalog, schema and table
      </bn-button>
    </div>`;
  }

  private renderPreview() {
    if (this.noPreview || this.disabled) return nothing;
    const preview = resolvePatterns(this.scope, this.values, this.tables, this.cacheEmpty);
    if (preview.state === "unconstrained") {
      return html`<p class="preview">
        No ${this.label.toLowerCase()} constraint, which matches every one.
      </p>`;
    }
    if (preview.state === "unknown") {
      return html`<p class="preview">
        No schema is cached for this instance, so what this pattern resolves to is unknown. Authoring
        is not blocked.${this.cacheWarning ? html` ${this.cacheWarning}` : nothing}
      </p>`;
    }
    if (preview.state === "none") {
      return html`<p class="preview none" role="status">
        Matches nothing. Every soft reference in the policy DSL fails by never matching rather than by
        erroring, so this pattern would protect nothing at all.
        <span class="advisory">
          Advisory preview — the instance's engine decides. It reflects the schema at the cache
          timestamp, and a later schema change can alter the outcome.
        </span>
      </p>`;
    }
    return html`<div class="preview" role="status">
      Matches ${preview.total} ${preview.total === 1 ? "object" : "objects"}${preview.total >
      preview.names.length
        ? html` (first ${preview.names.length} shown)`
        : nothing}.
      <ul class="names">
        ${preview.names.map((n) => html`<li>${n}</li>`)}
      </ul>
      <span class="advisory">
        Advisory preview — the instance's engine decides. It reflects the schema at the cache
        timestamp, and a later schema change can alter the outcome.${this.cacheWarning
          ? html` ${this.cacheWarning}`
          : nothing}
      </span>
    </div>`;
  }

  override render() {
    const listFinding = this.path ? worst(findingsAt(this.findings, this.path)) : null;
    return html`
      <bn-field
        label=${this.label}
        hint=${this.help}
        error=${listFinding?.message ?? ""}
        rule=${listFinding?.rule ?? ""}
        severity=${listFinding ? diagnosticSeverity(listFinding.severity) : "error"}
      >
        <div>
          ${this.rows.map((v, i) => this.renderRow(v, i))}
          ${this.grammar
            ? html`<details>
                <summary>Wildcard grammar</summary>
                <p>${this.grammar}</p>
              </details>`
            : nothing}
          ${this.renderPreview()}
        </div>
      </bn-field>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-pattern-list": PolicyPatternList;
  }
}
